import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Car, UtensilsCrossed, MapPin, Phone, MessageSquare, Send, Copy, X, AlertTriangle, Navigation, Star, Wifi, DollarSign, Clock, Check, ArrowDownCircle, ArrowUpCircle, Sparkles, Flame, Receipt, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import MapView from "@/components/chauffeur/MapView";
import useChauffeurSocket from "@/hooks/useChauffeurSocket";
import RatingModal from "@/components/chauffeur/RatingModal";
import FavoritesPicker from "@/components/chauffeur/FavoritesPicker";
import { RoutePolyline, RouteSummary } from "@/components/chauffeur/RouteOverlay";
import ManualPaymentModal from "@/components/payments/ManualPaymentModal";
import AddressAutocomplete from "@/components/chauffeur/AddressAutocomplete";
import AuthGate from "@/components/auth/AuthGate";
import CancelRideModal from "@/components/chauffeur/CancelRideModal";
import WaypointPanel from "@/components/chauffeur/WaypointPanel";
import HazardLayer from "@/components/chauffeur/HazardLayer";
import HazardReportButton from "@/components/chauffeur/HazardReportButton";
import MapIntelligenceChip from "@/components/chauffeur/MapIntelligenceChip";
import HotRightNow from "@/components/chauffeur/HotRightNow";
import RideChat from "@/components/chauffeur/RideChat";
import PickupPinReveal from "@/components/chauffeur/PickupPinReveal";
import SchedulePicker from "@/components/chauffeur/SchedulePicker";
import { errorMessage } from "@/lib/errors";

const STATUS_LABEL = {
  requested: "Searching for driver — review offers below",
  accepted: "Driver assigned — heading to you",
  arriving: "Driver is arriving",
  in_progress: "On the way",
  completed: "Trip complete",
  cancelled: "Cancelled",
};

const KINGSTON = { lat: 18.0179, lng: -76.8099 }; // sentinel only (legacy)
// Worldwide-default starting state. We try browser geolocation first; on
// denial/timeout the user can tap the map anywhere on Earth to drop pins.
const EMPTY_LOC = { lat: 0, lng: 0, address: "" };

// Same fare formula the backend uses — keeps client suggestion in sync.
function haversineKm(a, b) {
  const r = 6371, toRad = (d) => (d * Math.PI) / 180;
  const p1 = toRad(a.lat), p2 = toRad(b.lat);
  const dp = toRad(b.lat - a.lat), dl = toRad(b.lng - a.lng);
  const x = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(x));
}
function estimateFare(km, type) {
  const base = type === "taxi" ? 350 : 250;
  const perKm = type === "taxi" ? 180 : 140;
  return Math.round(base + perKm * km);
}

// Public quote endpoint — no auth so we can show it before sign-in.
async function axiosQuote(payload) {
  const res = await fetch(`${API_BASE}/api/chauffeur/rides/quote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("quote failed");
  return res.json();
}

export default function ChauffeurApp() {
  const [type, setType] = useState("taxi");
  const [pickup, setPickup] = useState(EMPTY_LOC);
  const [dropoff, setDropoff] = useState(EMPTY_LOC);
  const [pickingMode, setPickingMode] = useState("pickup");
  const [notes, setNotes] = useState("");
  const [foodItems, setFoodItems] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [activeRide, setActiveRide] = useState(null);
  const [offers, setOffers] = useState([]); // incoming driver offers (sorted by fare asc)
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [passengerOffer, setPassengerOffer] = useState(null);
  const [driverPin, setDriverPin] = useState(null);
  const [quote, setQuote] = useState(null);
  const [pendingRating, setPendingRating] = useState(null);
  const [payRide, setPayRide] = useState(null);  // ride to settle via manual payment
  const [tipRide, setTipRide] = useState(null);  // ride to tip
  // Phase-A InDrive-killer state ---------------------------------------
  const [scheduledFor, setScheduledFor] = useState(null);  // ISO 8601 string
  const [quietRide, setQuietRide] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [pickupCountry, setPickupCountry] = useState(null);  // ISO-3166-1 alpha-2
  const [authGate, setAuthGate] = useState(false);
  const [cancelRideOpen, setCancelRideOpen] = useState(false);
  const [authGateTab, setAuthGateTab] = useState("login");

  // ----------- Initial loads + geo -----------
  const loadActive = useCallback(async () => {
    try {
      const res = await api.get("/chauffeur/rides/me/active");
      setActiveRide(res.data);
      if (res.data?.driver_live?.current_lat != null) {
        setDriverPin({ lat: res.data.driver_live.current_lat, lng: res.data.driver_live.current_lng });
      }
    } catch { /* unauth */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try { const res = await api.get("/chauffeur/rides/me"); setHistory(res.data || []); } catch { /* */ }
  }, []);
  const loadOffers = useCallback(async (rideId) => {
    if (!rideId) return;
    try {
      const res = await api.get(`/chauffeur/rides/${rideId}/offers`);
      const sorted = [...(res.data || [])].sort((a, b) => a.fare - b.fare);
      setOffers(sorted);
    } catch { /* may be 404 if ride was just completed */ }
  }, []);

  useEffect(() => {
    loadActive(); loadHistory();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setPickup((s) => ({ ...s, lat: p.coords.latitude, lng: p.coords.longitude, address: s.address || "Current location" })),
        () => {}, { enableHighAccuracy: false, timeout: 4000 },
      );
    }
    // Deep-link from ARIA: ?type=taxi&pickup=...&dropoff=...&offer=600
    // Optionally with pre-resolved coordinates: &p_lat=…&p_lng=…&d_lat=…&d_lng=…
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("type")) setType(qs.get("type") === "food" ? "food" : "taxi");
    const pa = qs.get("pickup"), da = qs.get("dropoff"), ofr = qs.get("offer");
    const pLat = qs.get("p_lat"), pLng = qs.get("p_lng");
    const dLat = qs.get("d_lat"), dLng = qs.get("d_lng");
    if (pa) {
      setPickup((s) => ({
        ...s,
        address: pa,
        ...(pLat && pLng ? { lat: Number(pLat), lng: Number(pLng) } : {}),
      }));
    }
    if (da) {
      setDropoff({
        address: da,
        lat: dLat ? Number(dLat) : 0,
        lng: dLng ? Number(dLng) : 0,
      });
    }
    if (ofr && !Number.isNaN(Number(ofr))) setPassengerOffer(Number(ofr));
  }, [loadActive, loadHistory]);

  // ----------- Auto-suggested fare + Surge quote -----------
  const suggestedFare = useMemo(() => {
    if (!dropoff.lat || !dropoff.lng) return null;
    return estimateFare(haversineKm(pickup, dropoff), type);
  }, [pickup, dropoff, type]);

  // Pull live surge quote whenever pickup/dropoff/type stabilises.
  useEffect(() => {
    if (!dropoff.lat || !dropoff.lng) { setQuote(null); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const res = await axiosQuote({
          type,
          pickup_lat: pickup.lat, pickup_lng: pickup.lng,
          dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        });
        if (!cancel) setQuote(res);
      } catch { /* ignore */ }
    }, 600);
    return () => { cancel = true; clearTimeout(t); };
  }, [pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, type]);

  // Auto-detect the pickup's country (ISO-3166-1 alpha-2) so the dropoff
  // search biases to the same country by default — Jamaica → Jamaica,
  // NYC → US, Lagos → Nigeria. The result lives in `pickupCountry` and
  // is forwarded to <AddressAutocomplete country={...} />. Falls back to
  // worldwide if reverse-geocoding fails.
  useEffect(() => {
    if (!pickup.lat || !pickup.lng) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/chauffeur/locations/country", {
          params: { lat: pickup.lat, lng: pickup.lng },
        });
        if (!cancelled) setPickupCountry(res.data?.country || null);
      } catch { /* worldwide is fine */ }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pickup.lat, pickup.lng]);


  useEffect(() => {
    if (suggestedFare && passengerOffer == null) {
      // Default offer: 80% of system estimate (encourages negotiation)
      const surgedBase = quote?.estimated_fare || suggestedFare;
      setPassengerOffer(Math.max(50, Math.round(surgedBase * 0.8)));
    }
  }, [suggestedFare, quote?.estimated_fare]); // eslint-disable-line

  // ----------- Socket.IO for live updates -----------
  const { connected, on } = useChauffeurSocket({ rideId: activeRide?.id });

  useEffect(() => {
    if (!activeRide?.id) return;
    loadOffers(activeRide.id);
    const off1 = on("new_offer", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setOffers((prev) => {
        const without = prev.filter((o) => o.id !== payload.offer.id);
        return [...without, payload.offer].sort((a, b) => a.fare - b.fare);
      });
      toast.success(`New offer: JMD ${payload.offer.fare} from ${payload.offer.driver_name}`);
    });
    const off2 = on("offer_rejected", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setOffers((prev) => prev.map((o) => (o.id === payload.offer.id ? payload.offer : o)));
    });
    const off3 = on("offer_accepted", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      // Refresh ride snapshot — passenger or anyone in the room
      loadActive();
    });
    const off4 = on("ride_status", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setActiveRide((r) => (r ? { ...r, status: payload.status, driver_name: payload.driver_name || r.driver_name, driver_plate: payload.driver_plate || r.driver_plate, driver_vehicle: payload.driver_vehicle || r.driver_vehicle } : r));
      if (payload.status === "completed") {
        toast.success("Trip completed");
        setPendingRating({ ...activeRide, status: "completed" });
      }
      if (payload.status === "completed" || payload.status === "cancelled") {
        setTimeout(() => { setActiveRide(null); setOffers([]); loadHistory(); }, 1500);
      }
    });
    const off5 = on("driver_location", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setDriverPin({ lat: payload.lat, lng: payload.lng });
    });
    const off6 = on("priority_dispatch_summary", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setActiveRide((r) => (r ? { ...r, priority_until: payload.expires_at, priority_count: payload.count } : r));
    });
    const off7 = on("priority_expired", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setActiveRide((r) => (r ? { ...r, priority_until: null } : r));
    });
    const off8 = on("waypoint_added", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      // Refresh full ride doc so the new waypoint + fare are in sync.
      loadActive();
      if (payload.needs_driver_confirm) {
        toast.info(`Stop added — waiting for driver to confirm JMD ${payload.new_total}`);
      } else {
        toast.success(`Stop added — fare now JMD ${payload.new_total}`);
      }
    });
    const off9 = on("waypoint_removed", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      loadActive();
    });
    const off10 = on("waypoint_resolved", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      loadActive();
      if (payload.accepted) {
        toast.success(`Driver confirmed stop — fare JMD ${payload.agreed_fare}`);
      } else {
        toast.error("Driver declined the new stop — original route restored");
      }
    });
    const off11 = on("chat_message", (msg) => {
      if (msg.ride_id !== activeRide.id) return;
      const myEmail = (typeof window !== "undefined" && JSON.parse(localStorage.getItem("auth_user") || "{}").email) || null;
      // Only bump unread when chat panel is closed AND message isn't ours
      if (!chatOpen && msg.sender_email !== myEmail) {
        setUnreadChat((n) => n + 1);
        toast.message(
          msg.sender_role === "driver" ? "Driver" : "Rider",
          { description: msg.body || msg.quick_reply_text || "(quick reply)" }
        );
      }
    });
    const off12 = on("pickup_pin_verified", (payload) => {
      if (payload.ride_id !== activeRide.id) return;
      setActiveRide((r) => (r ? { ...r, pickup_pin_verified: true } : r));
    });
    return () => { off1(); off2(); off3(); off4(); off5(); off6(); off7(); off8(); off9(); off10(); off11(); off12(); };
  }, [activeRide?.id, on, loadActive, loadHistory, loadOffers, chatOpen]);

  // ----------- Actions -----------
  const onMapClick = (latlng) => {
    if (pickingMode === "pickup") {
      setPickup((s) => ({ ...s, lat: latlng.lat, lng: latlng.lng }));
      toast.success("Pickup pin set");
    } else {
      setDropoff({ lat: latlng.lat, lng: latlng.lng, address: dropoff.address });
      toast.success("Drop-off pin set");
    }
    setPassengerOffer(null); // recompute on new pins
  };

  // Reset unread badge when chat opens
  useEffect(() => {
    if (chatOpen) setUnreadChat(0);
  }, [chatOpen]);

  const submitRide = async () => {
    if (!pickup.address || !dropoff.address) { toast.error("Enter pickup & drop-off addresses"); return; }
    if (!dropoff.lat || !dropoff.lng) { toast.error("Tap the map to set drop-off pin"); return; }
    if (!passengerOffer || passengerOffer < 50) { toast.error("Enter a fair offer (min JMD 50)"); return; }
    // Guests must sign in before posting — open the inline AuthGate so the
    // half-filled form (pickup, dropoff, notes, offer) survives the login.
    const tok = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
    if (!tok || tok === "pending") { setAuthGate(true); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/chauffeur/rides/request", {
        type,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng, pickup_address: pickup.address,
        dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng, dropoff_address: dropoff.address,
        notes,
        food_items: type === "food" ? foodItems : null,
        restaurant_name: type === "food" ? restaurant : null,
        passenger_offer: Number(passengerOffer),
        scheduled_for: scheduledFor || null,
        quiet_ride: quietRide,
      });
      setActiveRide(res.data);
      setOffers([]);
      setScheduledFor(null);
      setQuietRide(false);
      if (res.data.status === "scheduled") {
        toast.success(`Ride scheduled for ${new Date(res.data.scheduled_for).toLocaleString()} — we'll dispatch 10 min before`);
      } else {
        toast.success(`Posted at JMD ${res.data.passenger_offer} — drivers can accept or counter`);
      }
    } catch (err) {
      // Server-side auth check (token expired between submit clicks)
      if (err.response?.status === 401) { setAuthGate(true); return; }
      toast.error(errorMessage(err, "Could not request ride"));
    } finally { setSubmitting(false); }
  };

  const acceptOffer = async (offerId) => {
    try {
      const res = await api.post(`/chauffeur/offers/${offerId}/accept`);
      setActiveRide(res.data.ride);
      setOffers((prev) => prev.map((o) => (o.id === offerId ? res.data.offer : { ...o, status: "rejected" })));
      toast.success(`Accepted — driver inbound at JMD ${res.data.ride.agreed_fare}`);
    } catch (err) {
      toast.error(errorMessage(err, "Accept failed"));
    }
  };
  const rejectOffer = async (offerId) => {
    try {
      const res = await api.post(`/chauffeur/offers/${offerId}/reject`);
      setOffers((prev) => prev.map((o) => (o.id === offerId ? res.data : o)));
    } catch (err) { toast.error(errorMessage(err, "Reject failed")); }
  };

  const bumpOffer = async (newOffer) => {
    if (!activeRide) return;
    try {
      const res = await api.patch(`/chauffeur/rides/${activeRide.id}/passenger-offer`, { new_offer: Number(newOffer) });
      setActiveRide(res.data);
      toast.success(`Offer bumped to JMD ${res.data.passenger_offer}`);
    } catch (err) { toast.error(errorMessage(err, "Bump failed")); }
  };

  const aiSuggest = async () => {
    if (!activeRide) return null;
    try {
      const res = await api.post(`/chauffeur/rides/${activeRide.id}/ai-suggest-fare`);
      return res.data;
    } catch { toast.error("AI suggestion unavailable"); return null; }
  };

  const broadcastNow = async () => {
    if (!activeRide) return;
    try {
      await api.post(`/chauffeur/rides/${activeRide.id}/broadcast-now`);
      setActiveRide((r) => (r ? { ...r, priority_until: null } : r));
      toast.success("Open to all nearby drivers");
    } catch (err) {
      toast.error(errorMessage(err, "Could not broadcast"));
    }
  };
  const triggerSos = async () => {
    if (!activeRide) return;
    if (!window.confirm("Trigger SOS? Admin will be alerted immediately.")) return;
    try {
      await api.post(`/chauffeur/rides/${activeRide.id}/sos`, {
        lat: pickup.lat, lng: pickup.lng, note: "Emergency triggered from passenger app",
      });
      toast.success("SOS sent — admin notified");
    } catch (err) { toast.error(errorMessage(err, "SOS failed")); }
  };
  const copyShare = async () => {
    if (!activeRide?.share_token) return;
    const url = `${window.location.origin}/trip/${activeRide.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); } catch { toast.error("Could not copy"); }
  };

  return (
    <div data-testid="chauffeur-page" className="pt-24 min-h-screen bg-luxury-black">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Driver · Delivery · Passenger Hub</p>
          <div className="flex items-end justify-between gap-4 mb-1">
            <h1 className="font-heading text-3xl text-white">Aristocrats <span className="italic text-gold">Chauffeur</span></h1>
            <div data-testid="rt-status" className={`flex items-center gap-1.5 text-[10px] font-body uppercase tracking-wider ${connected ? "text-green-400" : "text-white/30"}`}>
              <Wifi size={10} /> {connected ? "Live" : "Offline"}
            </div>
          </div>
          <p className="text-white/50 font-body text-sm">Book a taxi, order delivery, or sign up to drive — all from one place.</p>
        </motion.div>

        {/* AI map intelligence — live "what the platform has learned" chip */}
        <MapIntelligenceChip />

        {/* 🔥 Hot Right Now — top 3 trending pickup zones (last 60 min) */}
        <HotRightNow minutes={60} limit={3} />

        {/* MODE HUB — three prominent tabs so people don't get lost */}
        {!activeRide && (
          <ModeHub current={type} onChange={(t) => setType(t)} />
        )}

        {activeRide && !["completed", "cancelled"].includes(activeRide.status) ? (
          activeRide.status === "requested" ? (
            <RequestedView
              ride={activeRide}
              offers={offers}
              onAccept={acceptOffer}
              onReject={rejectOffer}
              onBump={bumpOffer}
              onAiSuggest={aiSuggest}
              onShare={copyShare}
              onSos={triggerSos}
              onBroadcastNow={broadcastNow}
            />
          ) : (
            <ActiveRidePanel
              ride={activeRide}
              driverPin={driverPin}
              onSos={triggerSos}
              onCopy={copyShare}
              onCancel={() => setCancelRideOpen(true)}
              onRideUpdate={(r) => setActiveRide(r)}
              myEmail={(typeof window !== "undefined" && JSON.parse(localStorage.getItem("auth_user") || "{}").email) || null}
              chatOpen={chatOpen}
              setChatOpen={setChatOpen}
              unreadChat={unreadChat}
            />
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {/* Guest-friendly sign-in banner — appears only if no valid token. */}
              {(() => {
                const tok = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
                if (tok && tok !== "pending") return null;
                return (
                  <div data-testid="chauffeur-guest-banner"
                    className="flex flex-wrap items-center justify-between gap-3 p-3 border border-gold/30 bg-gold/5 rounded-md">
                    <div className="text-xs">
                      <span className="text-gold font-semibold">Sign in to book.</span>
                      <span className="text-white/60"> 10 seconds — your form will be saved.</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="chauffeur-signin-btn"
                        onClick={() => { setAuthGateTab("login"); setAuthGate(true); }}
                        className="text-xs text-gold/80 hover:text-gold border border-white/15 hover:border-gold/40 rounded-md px-3 py-1.5 transition-colors"
                      >Sign in</button>
                      <button
                        data-testid="chauffeur-signup-btn"
                        onClick={() => { setAuthGateTab("register"); setAuthGate(true); }}
                        className="inline-flex items-center gap-1.5 text-xs text-luxury-black bg-gold hover:bg-gold/90 rounded-md px-3 py-1.5 transition-colors"
                      ><Mail size={12}/> Sign up with email</button>
                    </div>
                  </div>
                );
              })()}
              {quote?.surge_active && (
                <div data-testid="surge-banner" className="flex items-center gap-3 p-3 border border-orange-500/40 bg-orange-500/10 rounded-md">
                  <Flame className="text-orange-400 shrink-0" size={18} />
                  <div className="flex-1">
                    <p className="text-orange-300 text-xs font-body uppercase tracking-wider">Surge {quote.surge_multiplier}x — high demand</p>
                    <p className="text-white/60 text-[10px] font-body">{quote.open_requests_nearby} open requests · {quote.drivers_available_nearby} drivers nearby</p>
                  </div>
                </div>
              )}
              <FavoritesPicker
                onPick={(loc) => {
                  if (pickingMode === "pickup") {
                    setPickup({ lat: loc.lat, lng: loc.lng, address: loc.address });
                  } else {
                    setDropoff({ lat: loc.lat, lng: loc.lng, address: loc.address });
                  }
                  toast.success(`${pickingMode === "pickup" ? "Pickup" : "Drop-off"} set`);
                }}
                currentLat={pickingMode === "pickup" ? pickup.lat : dropoff.lat}
                currentLng={pickingMode === "pickup" ? pickup.lng : dropoff.lng}
                currentAddress={pickingMode === "pickup" ? pickup.address : dropoff.address}
              />
              <div>
                <button data-testid="type-taxi" onClick={() => setType("taxi")}
                  className={`hidden ${type === "taxi" ? "" : ""}`}>taxi</button>
                <button data-testid="type-food" onClick={() => setType("food")}
                  className={`hidden ${type === "food" ? "" : ""}`}>food</button>
              </div>
              <AddressAutocomplete
                data-testid="pickup-autocomplete"
                label="Pickup"
                placeholder="Type an address (e.g. Hope Road Kingston, Times Square NYC, Tokyo)…"
                value={pickup}
                onChange={(next) => setPickup({ ...pickup, ...next })}
                showUseCurrent
                country={pickupCountry}
                nearLat={pickup.lat || null}
                nearLng={pickup.lng || null}
                kind="pickup"
              />
              <AddressAutocomplete
                data-testid="dropoff-autocomplete"
                label="Drop-off"
                placeholder={pickupCountry
                  ? `Type an address — biased to your pickup's country`
                  : `Type an address worldwide…`}
                value={dropoff}
                onChange={(next) => setDropoff({ ...dropoff, ...next })}
                country={pickupCountry}
                nearLat={pickup.lat || null}
                nearLng={pickup.lng || null}
                kind="dropoff"
              />
              {type === "food" && (
                <>
                  <Input data-testid="restaurant-name" value={restaurant}
                    onChange={(e) => setRestaurant(e.target.value)}
                    placeholder="Restaurant name" className="bg-white/5 border-white/15 text-white" />
                  <Textarea data-testid="food-items" value={foodItems} rows={3}
                    onChange={(e) => setFoodItems(e.target.value)}
                    placeholder="Order items, e.g. 1 jerk chicken, 2 patties" className="bg-white/5 border-white/15 text-white" />
                </>
              )}
              <Textarea data-testid="ride-notes" value={notes} rows={2}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes for the driver (gate code, allergies…)" className="bg-white/5 border-white/15 text-white" />

              <div className="p-4 border border-gold/30 bg-gold/5 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gold text-xs font-body uppercase tracking-wider flex items-center gap-1"><DollarSign size={12} /> Your Offer (JMD)</label>
                  {(quote?.estimated_fare || suggestedFare) && (
                    <span className="text-white/40 text-[10px] font-body">
                      est. JMD {quote?.estimated_fare || suggestedFare}
                      {quote?.surge_active && <span className="text-orange-300 ml-1">· {quote.surge_multiplier}x surge</span>}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="offer-decrease" disabled={!passengerOffer} onClick={() => setPassengerOffer((v) => Math.max(50, (v || 0) - 50))}
                    className="text-gold/70 hover:text-gold transition-colors disabled:opacity-30">
                    <ArrowDownCircle size={20} />
                  </button>
                  <Input data-testid="passenger-offer-input" type="number" min={50} step={10}
                    value={passengerOffer ?? ""} onChange={(e) => setPassengerOffer(e.target.value ? Number(e.target.value) : null)}
                    className="bg-luxury-black/60 border-gold/30 text-gold font-heading text-xl text-center"
                    placeholder={suggestedFare ? String(suggestedFare) : "—"} />
                  <button data-testid="offer-increase" disabled={!passengerOffer} onClick={() => setPassengerOffer((v) => (v || 0) + 50)}
                    className="text-gold/70 hover:text-gold transition-colors disabled:opacity-30">
                    <ArrowUpCircle size={20} />
                  </button>
                </div>
                <p className="text-white/40 text-[10px] font-body mt-2">Lower than estimate = save money but may take longer to match.</p>
              </div>

              <div className="flex gap-2 text-xs">
                <button data-testid="pin-mode-pickup" onClick={() => setPickingMode("pickup")}
                  className={`flex-1 py-2 border ${pickingMode === "pickup" ? "border-gold/50 text-gold bg-gold/10" : "border-white/15 text-white/60"}`}>
                  Tap map → set Pickup
                </button>
                <button data-testid="pin-mode-dropoff" onClick={() => setPickingMode("dropoff")}
                  className={`flex-1 py-2 border ${pickingMode === "pickup" ? "border-white/15 text-white/60" : "border-gold/50 text-gold bg-gold/10"}`}>
                  Tap map → set Drop-off
                </button>
              </div>

              {/* Phase-A: schedule for later */}
              <SchedulePicker value={scheduledFor} onChange={setScheduledFor} />

              {/* Phase-A: quiet ride toggle */}
              <label data-testid="quiet-ride-toggle"
                className="flex items-center justify-between gap-3 p-3 border border-white/10 bg-black/30 rounded-md cursor-pointer hover:border-purple-400/30 transition-colors">
                <div>
                  <p className="text-[11px] text-white uppercase tracking-wider">Quiet Ride</p>
                  <p className="text-[10px] text-white/45">Driver sees a small "quiet ride" badge — no chitchat preferred.</p>
                </div>
                <input data-testid="quiet-ride-input" type="checkbox" checked={quietRide}
                  onChange={(e) => setQuietRide(e.target.checked)}
                  className="w-5 h-5 accent-purple-400" />
              </label>

              <button data-testid="request-ride-btn" disabled={submitting} onClick={submitRide}
                className="w-full flex items-center justify-center gap-2 bg-gold text-luxury-black py-4 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-50">
                <Send size={14} /> {submitting ? "Posting…" : scheduledFor ? `Schedule @ JMD ${passengerOffer || "—"}` : `Post @ JMD ${passengerOffer || "—"}`}
              </button>
            </div>
            <MapView pickup={pickup} dropoff={dropoff.lat ? dropoff : null}
              onClick={onMapClick} height={620} data-testid="ride-map">
              <HazardLayer center={pickup} radiusKm={25} allowConfirm />
            </MapView>
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-12">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-3">Recent Trips</p>
            <div className="space-y-2">
              {history.slice(0, 8).map((r) => (
                <div key={r.id} data-testid={`history-${r.id}`} className="flex items-center justify-between p-3 border border-white/10 bg-white/5 text-xs font-body">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.type === "food" ? <UtensilsCrossed size={14} className="text-gold shrink-0" /> : <Car size={14} className="text-gold shrink-0" />}
                    <span className="text-white truncate">{r.pickup?.address || "—"} → {r.dropoff?.address || "—"}</span>
                  </div>
                  <div className="text-white/50 flex items-center gap-3 shrink-0">
                    <span>JMD {r.agreed_fare || r.estimated_fare}</span>
                    <span className={`px-2 py-0.5 ${r.status === "completed" ? "text-green-400 bg-green-400/10" : "text-white/60 bg-white/10"}`}>
                      {r.status}
                    </span>
                    {r.status === "completed" && (
                      <button data-testid={`receipt-${r.id}`}
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await api.get(`/chauffeur/rides/${r.id}/receipt`, { responseType: "blob" });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            window.open(url, "_blank", "noopener");
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                          } catch { toast.error("Could not load receipt"); }
                        }}
                        className="text-gold hover:underline inline-flex items-center gap-1">
                        <Receipt size={12} /> Receipt
                      </button>
                    )}
                    {r.status === "completed" && r.payment_status !== "paid" && (
                      <button
                        data-testid={`pay-ride-${r.id}`}
                        onClick={(e) => { e.preventDefault(); setPayRide(r); }}
                        className="text-gold hover:underline inline-flex items-center gap-1"
                      >
                        <DollarSign size={12} /> Pay
                      </button>
                    )}
                    {r.status === "completed" && r.driver_name && (
                      <button
                        data-testid={`tip-ride-${r.id}`}
                        onClick={(e) => { e.preventDefault(); setTipRide(r); }}
                        className="text-amber-300 hover:underline inline-flex items-center gap-1"
                        title={`Tip ${r.driver_name}`}
                      >
                        <Sparkles size={12} /> Tip
                      </button>
                    )}
                    {r.status === "completed" && r.driver_phone && (
                      <a
                        data-testid={`call-history-${r.id}`}
                        href={`tel:${r.driver_phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-emerald-300 hover:underline inline-flex items-center gap-1"
                        aria-label="Call driver"
                      >
                        <Phone size={12} /> Call
                      </a>
                    )}
                    {r.status === "completed" && r.driver_phone && (
                      <a
                        data-testid={`text-history-${r.id}`}
                        href={`sms:${r.driver_phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sky-300 hover:underline inline-flex items-center gap-1"
                        aria-label="Text driver"
                      >
                        <MessageSquare size={12} /> Text
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {pendingRating && (
        <RatingModal ride={pendingRating} role="passenger"
          onClose={() => setPendingRating(null)} onRated={() => setPendingRating(null)} />
      )}
      <ManualPaymentModal
        open={!!payRide}
        onClose={() => setPayRide(null)}
        amount={payRide?.agreed_fare || payRide?.estimated_fare || 0}
        currency="JMD"
        purpose="chauffeur_ride"
        purposeRefId={payRide?.id || null}
        defaultNote={payRide ? `Ride ${String(payRide.id).slice(0, 8)} · ${payRide.pickup?.address || ""}` : ""}
        onApproved={() => { setPayRide(null); loadHistory(); }}
      />
      {/* Tip-the-driver flow — same modal, purpose="tip" so the registry
          attributes it back to the linked ride. */}
      <ManualPaymentModal
        open={!!tipRide}
        onClose={() => setTipRide(null)}
        amount={
          // Sensible default tip = 15% of the ride fare, rounded to nearest 50 JMD
          tipRide
            ? Math.max(50, Math.round(((tipRide.agreed_fare || tipRide.estimated_fare || 500) * 0.15) / 50) * 50)
            : 0
        }
        currency="JMD"
        purpose="tip"
        purposeRefId={tipRide?.id || null}
        defaultNote={tipRide ? `Tip for ${tipRide.driver_name || "driver"} · ride ${String(tipRide.id).slice(0, 8)}` : ""}
        onApproved={() => { setTipRide(null); loadHistory(); }}
      />
      <AuthGate
        open={authGate}
        onClose={() => setAuthGate(false)}
        defaultTab={authGateTab}
        reason="Your pickup, drop-off, notes, and offer are saved. Sign in (or sign up with email — takes 10 seconds) and we'll post your ride."
        onSuccess={() => { setAuthGate(false); submitRide(); }}
      />
      <CancelRideModal
        ride={activeRide}
        open={cancelRideOpen}
        onClose={() => setCancelRideOpen(false)}
        onCancelled={() => { setActiveRide(null); loadHistory(); }}
      />
    </div>
  );
}

// ============== Sub-components ==============

function ModeHub({ current, onChange }) {
  const isLoggedIn = typeof window !== "undefined" &&
    !!localStorage.getItem("auth_token") &&
    localStorage.getItem("auth_token") !== "pending";
  const driveTarget = isLoggedIn ? "/driver" : "/driver/signup";
  const tabs = [
    { id: "taxi",  Icon: Car,             label: "I need a ride",     desc: "Door-to-door, name your fare" },
    { id: "food",  Icon: UtensilsCrossed, label: "I need a delivery", desc: "Food, parcels, errands" },
    { id: "drive", Icon: Wifi,            label: "I want to drive",   desc: "Earn driving or delivering" },
  ];
  return (
    <div data-testid="chauffeur-mode-hub" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
      {tabs.map((tab) => {
        const active = current === tab.id;
        if (tab.id === "drive") {
          return (
            <Link key={tab.id} to={driveTarget} data-testid={`mode-hub-${tab.id}`}
              className="group block p-5 border border-white/10 hover:border-gold/40 bg-white/[0.02] hover:bg-gold/[0.04] transition-colors">
              <tab.Icon className="text-gold mb-2" size={22} />
              <p className="font-heading text-lg text-white">{tab.label}</p>
              <p className="text-white/50 font-body text-xs mt-1">{tab.desc}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-gold text-[10px] font-body uppercase tracking-wider opacity-70 group-hover:opacity-100">
                {isLoggedIn ? "Open driver console →" : "Apply now →"}
              </span>
            </Link>
          );
        }
        return (
          <button key={tab.id} data-testid={`mode-hub-${tab.id}`} onClick={() => onChange(tab.id)}
            className={`group text-left p-5 border transition-colors ${
              active
                ? "border-gold bg-gold/10"
                : "border-white/10 hover:border-gold/40 bg-white/[0.02] hover:bg-gold/[0.04]"
            }`}>
            <tab.Icon className={active ? "text-gold mb-2" : "text-gold/70 mb-2"} size={22} />
            <p className="font-heading text-lg text-white">{tab.label}</p>
            <p className="text-white/50 font-body text-xs mt-1">{tab.desc}</p>
            {active && (
              <span className="mt-3 inline-flex items-center gap-1 text-gold text-[10px] font-body uppercase tracking-wider">✓ Selected</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function RequestedView({ ride, offers, onAccept, onReject, onShare, onSos, onBump, onAiSuggest, onBroadcastNow }) {
  const pendingOffers = offers.filter((o) => o.status === "pending");
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [prioritySecs, setPrioritySecs] = useState(null);

  // Live countdown for priority window
  useEffect(() => {
    if (!ride.priority_until) { setPrioritySecs(null); return; }
    const tick = () => {
      const left = Math.max(0, Math.round((new Date(ride.priority_until).getTime() - Date.now()) / 1000));
      setPrioritySecs(left);
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [ride.priority_until]);

  // Auto-prompt AI bump after 30s with no pending offers (no auto-fetch — user
  // must tap to call GPT-5.2, keeps cost predictable).
  useEffect(() => {
    if (pendingOffers.length > 0) { setShowAiPrompt(false); return; }
    const t = setTimeout(() => setShowAiPrompt(true), 30000);
    return () => clearTimeout(t);
  }, [pendingOffers.length]); // eslint-disable-line

  const fetchAi = async () => {
    setAiLoading(true);
    const r = await onAiSuggest();
    setAiSuggestion(r);
    setAiLoading(false);
  };

  const QUICK_BUMPS = [50, 100, 200];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <div data-testid="active-ride-status" className="p-5 border border-gold/30 bg-gold/5 rounded-md">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-2">Status</p>
          <p className="font-heading text-xl text-white">{STATUS_LABEL[ride.status]}</p>
          <p className="text-white/50 text-xs font-body mt-1">{ride.distance_km} km · your offer JMD {ride.passenger_offer}</p>
        </div>

        {/* Priority dispatch pill */}
        {prioritySecs != null && prioritySecs > 0 && (
          <div data-testid="priority-status-pill" className="p-3 border border-gold/40 bg-gold/10 rounded-md">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold flex items-center gap-1 mb-1">
              <Sparkles size={10} /> Priority Dispatch · {prioritySecs}s
            </p>
            <p className="text-white text-xs font-body">{ride.priority_count || 5} top-rated drivers nearby got first-look.</p>
            <button data-testid="broadcast-now-btn" onClick={onBroadcastNow}
              className="mt-2 w-full text-[11px] font-body uppercase tracking-wider text-gold hover:text-white transition-colors py-1">
              Open to all drivers now →
            </button>
          </div>
        )}

        {/* Quick bump buttons + AI prompt */}
        <div data-testid="bump-controls" className="p-4 border border-gold/20 bg-gold/[0.03] rounded-md space-y-3">
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/80 flex items-center gap-1">
            <ArrowUpCircle size={10} /> Boost your offer
          </p>
          <div className="flex gap-2">
            {QUICK_BUMPS.map((b) => (
              <button key={b} data-testid={`quick-bump-${b}`}
                onClick={() => onBump((ride.passenger_offer || 0) + b)}
                className="flex-1 py-2 text-xs font-body border border-gold/30 text-gold hover:bg-gold/10 transition-colors">
                +{b}
              </button>
            ))}
          </div>
          {showAiPrompt && !aiSuggestion && (
            <button data-testid="ai-suggest-btn" onClick={fetchAi} disabled={aiLoading}
              className="w-full flex items-center justify-center gap-2 py-2 text-xs font-body bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 transition-colors disabled:opacity-50">
              <Sparkles size={12} /> {aiLoading ? "Asking AI…" : "Ask AI for a smart bump"}
            </button>
          )}
          {aiSuggestion && (
            <div data-testid="ai-suggestion-card" className="p-3 border border-gold/40 bg-luxury-black rounded-sm">
              <p className="text-[10px] font-body uppercase tracking-wider text-gold mb-1 flex items-center gap-1">
                <Sparkles size={10} /> AI Suggestion {aiSuggestion.source === "ai" ? "(GPT-5.2)" : ""}
              </p>
              <p className="font-heading text-2xl text-gold">JMD {aiSuggestion.suggested_offer}</p>
              <p className="text-white/60 text-[11px] font-body mt-1">{aiSuggestion.rationale}</p>
              <button data-testid="ai-bump-apply" onClick={() => { onBump(aiSuggestion.suggested_offer); setAiSuggestion(null); }}
                className="mt-2 w-full bg-gold text-luxury-black py-1.5 text-[11px] font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
                Apply &amp; rebroadcast
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border border-white/10 bg-white/5 rounded-md">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-white/50 mb-1">Pickup</p>
          <p className="text-white text-sm font-body">{ride.pickup?.address}</p>
          <p className="text-xs font-body uppercase tracking-[0.2em] text-white/50 mt-3 mb-1">Drop-off</p>
          <p className="text-white text-sm font-body">{ride.dropoff?.address}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="sos-btn" onClick={onSos}
            className="flex items-center justify-center gap-2 bg-red-500/20 text-red-300 py-3 text-xs font-body uppercase tracking-wider border border-red-500/40 hover:bg-red-500/30 transition-colors">
            <AlertTriangle size={14} /> SOS
          </button>
          <button data-testid="share-trip-btn" onClick={onShare}
            className="flex items-center justify-center gap-2 bg-white/5 text-white py-3 text-xs font-body uppercase tracking-wider border border-white/15 hover:bg-white/10 transition-colors">
            <Copy size={14} /> Share
          </button>
        </div>
        {/* While waiting for offers, the passenger can already drop a community hazard pin on their pickup area. */}
        <div data-testid="requested-hazard-strip" className="p-3 border border-amber-500/20 bg-amber-500/5 rounded-md flex items-center justify-between gap-3">
          <p className="text-[11px] text-amber-300/80">Notice police or a delay nearby? Help drivers route around it.</p>
          <HazardReportButton
            lat={ride.pickup?.lat || 0}
            lng={ride.pickup?.lng || 0}
            compact
          />
        </div>
      </div>
      <div className="lg:col-span-2">
        <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-3 flex items-center gap-2">
          Live Offers
          <span data-testid="offers-count" className="bg-gold/20 text-gold px-2 py-0.5 text-[10px]">{pendingOffers.length}</span>
        </p>
        {pendingOffers.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-md p-12 text-center">
            <div className="inline-block animate-pulse">
              <Car className="mx-auto mb-2 text-gold/60" size={28} />
              <p className="text-white/50 font-body text-sm">Waiting for nearby drivers to bid…</p>
              <p className="text-white/30 text-xs font-body mt-1">Drivers can accept your price or counter with their own</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence initial={false}>
              {pendingOffers.map((o) => {
                const counter = o.fare !== ride.passenger_offer;
                return (
                  <motion.div key={o.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    data-testid={`offer-${o.id}`}
                    className="p-4 border border-white/10 bg-white/5 rounded-md flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center font-heading">
                        {o.driver_name?.charAt(0)?.toUpperCase() || "D"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-body truncate">{o.driver_name} <span className="text-white/40">· ⭐ {o.driver_rating?.toFixed(1) || "5.0"}</span></p>
                        <p className="text-white/50 text-xs font-body truncate">{o.driver_vehicle} · <span className="text-gold">{o.driver_plate}</span></p>
                        <p className="text-white/40 text-[10px] font-body flex items-center gap-2">
                          <Clock size={10} /> ~{o.eta_minutes} min
                          {o.distance_to_pickup_km != null && <span>· {o.distance_to_pickup_km} km away</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="font-heading text-2xl text-gold">JMD {o.fare}</p>
                      {counter && <span className="text-[10px] font-body uppercase tracking-wider text-amber-300">Counter-offer</span>}
                      <div className="flex gap-2">
                        <button data-testid={`accept-offer-${o.id}`} onClick={() => onAccept(o.id)}
                          className="bg-gold text-luxury-black px-3 py-1.5 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors flex items-center gap-1">
                          <Check size={12} /> Accept
                        </button>
                        <button data-testid={`reject-offer-${o.id}`} onClick={() => onReject(o.id)}
                          className="text-red-400/70 hover:text-red-400 px-2 py-1.5 transition-colors">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 4-step delivery progress timeline for food/delivery rides:
 *   1. Order placed   (status === requested)
 *   2. Picked up       (status === arriving)         — driver is en route
 *   3. On the way      (status === in_progress)      — out for delivery
 *   4. Delivered       (status === completed)
 * The progress bar is driven by the driver's distance to the dropoff —
 * % closes as they get closer.
 */
function DeliveryProgressTimeline({ ride, driverKm }) {
  const STEPS = [
    { key: "requested",    label: "Order placed" },
    { key: "accepted",     label: "Driver assigned" },
    { key: "arriving",     label: "Picked up" },
    { key: "in_progress",  label: "On the way" },
    { key: "completed",    label: "Delivered" },
  ];
  // Map ride.status → step index
  const stepIdx = STEPS.findIndex((s) => s.key === ride.status);
  const reachedIdx = stepIdx === -1 ? 0 : stepIdx;
  // Distance-based % within the in_progress phase. We assume ride.distance_km
  // is the original total; remaining = driverKm. Floor at 0, cap at 100.
  let progressPct = 0;
  if (ride.status === "completed") progressPct = 100;
  else if (ride.status === "in_progress" && driverKm != null && ride.distance_km) {
    const remaining = Math.max(0, driverKm);
    const total = Math.max(0.5, ride.distance_km);
    progressPct = Math.min(100, Math.max(0, Math.round((1 - remaining / total) * 100)));
  } else if (reachedIdx > 0) {
    progressPct = Math.round((reachedIdx / (STEPS.length - 1)) * 100);
  }
  return (
    <div data-testid="delivery-timeline" className="p-4 border border-gold/20 bg-white/5 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-body uppercase tracking-wider text-gold">Delivery progress</p>
        <span className="text-xs text-white/60">{progressPct}%</span>
      </div>
      <div className="w-full h-1.5 bg-black/40 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-gold to-amber-400 transition-all"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <ol className="space-y-1.5">
        {STEPS.map((s, i) => {
          const done = i <= reachedIdx;
          return (
            <li key={s.key} className="flex items-center gap-2 text-xs">
              <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                done ? "bg-gold" : "bg-white/15 border border-white/25"
              }`}/>
              <span className={done ? "text-white" : "text-white/40"}>{s.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ActiveRidePanel({ ride, driverPin, onSos, onCopy, onCancel, onRideUpdate, myEmail, chatOpen, setChatOpen, unreadChat }) {
  // Live distance from driver to pickup (or to passenger's current pin if
  // they're now in the car) — Uber-style follow-along. Updates whenever
  // driverPin changes (Socket.IO push).
  const driverAnchor = ride.status === "in_progress" ? ride.dropoff : ride.pickup;
  const driverKm = (() => {
    if (!driverPin || !driverAnchor || !driverAnchor.lat) return null;
    return haversineKm(driverPin, driverAnchor);
  })();
  // Conservative ETA: 30 km/h average in city traffic.
  const driverEtaMin = driverKm != null ? Math.max(1, Math.round(driverKm * 2)) : null;
  const driverHeadingLabel = ride.status === "in_progress" ? "to drop-off" : "to pickup";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <div data-testid="active-ride-status" className="p-5 border border-gold/30 bg-gold/5 rounded-md">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-2">Status</p>
          <p className="font-heading text-xl text-white">{STATUS_LABEL[ride.status] || ride.status}</p>
          <p className="text-white/50 text-xs font-body mt-1">{ride.distance_km} km · agreed JMD {ride.agreed_fare || ride.estimated_fare}</p>
        </div>
        {/* Live driver-distance card — only when we know where the driver is. */}
        {driverKm != null && (
          <div data-testid="driver-distance-card" className="p-4 border border-emerald-500/30 bg-emerald-500/5 rounded-md">
            <div className="flex items-center justify-between">
              <p className="text-xs font-body uppercase tracking-wider text-emerald-300">Driver {driverHeadingLabel}</p>
              <span className="text-[10px] text-emerald-300/70 inline-flex items-center gap-1"><Wifi size={10}/> live</span>
            </div>
            <p className="text-2xl text-white font-display mt-2">{driverKm.toFixed(driverKm < 10 ? 1 : 0)} km away</p>
            <p className="text-xs text-white/60 mt-1">ETA ~{driverEtaMin} min</p>
          </div>
        )}
        <RouteSummary rideId={ride.id} />
        {ride.driver_name && (
          <div className="p-4 border border-gold/20 bg-white/5 rounded-md space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {ride.driver_avatar_url ? (
                  <img
                    src={ride.driver_avatar_url}
                    alt={ride.driver_name}
                    className="w-14 h-14 rounded-full object-cover border border-gold/40 flex-shrink-0"
                    data-testid="driver-avatar"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full border border-gold/40 bg-black/40 flex items-center justify-center flex-shrink-0 text-gold text-xl font-display">
                    {(ride.driver_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-white text-sm font-body font-medium truncate">{ride.driver_name}</p>
                  <p className="text-white/50 text-xs font-body truncate">{ride.driver_vehicle}</p>
                  <p className="text-gold text-xs font-body uppercase tracking-wider mt-0.5">{ride.driver_plate}</p>
                </div>
              </div>
              {ride.driver_phone && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <a
                    data-testid="call-driver-btn"
                    href={`tel:${ride.driver_phone}`}
                    aria-label="Call driver"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-luxury-black shadow-lg shadow-emerald-500/30 transition-all hover:scale-105"
                  >
                    <Phone size={20} />
                  </a>
                  <a
                    data-testid="text-driver-btn"
                    href={`sms:${ride.driver_phone}${
                      // iOS uses ?body, Android uses ?body too (works on both); harmless on desktop
                      `?&body=${encodeURIComponent(`Hi ${(ride.driver_name || "").split(" ")[0] || "there"} — about ride ${ride.id?.slice(0,8) || ""}`)}`
                    }`}
                    aria-label="Text driver"
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-500 hover:bg-sky-400 text-luxury-black shadow-lg shadow-sky-500/30 transition-all hover:scale-105"
                  >
                    <MessageSquare size={20} />
                  </a>
                </div>
              )}
            </div>
            {/* Screenshot-friendly safety strip — vehicle + plate photo */}
            {(ride.driver_vehicle_photo_url || ride.driver_license_photo_url) && (
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                {ride.driver_vehicle_photo_url && (
                  <a href={ride.driver_vehicle_photo_url} target="_blank" rel="noreferrer" data-testid="driver-vehicle-photo">
                    <img
                      src={ride.driver_vehicle_photo_url}
                      alt="Vehicle"
                      className="w-full h-20 object-cover rounded border border-white/10 hover:border-gold/40 transition-colors"
                    />
                    <p className="text-[10px] text-white/45 mt-0.5 text-center">Vehicle</p>
                  </a>
                )}
                {ride.driver_license_photo_url && (
                  <a href={ride.driver_license_photo_url} target="_blank" rel="noreferrer" data-testid="driver-plate-photo">
                    <img
                      src={ride.driver_license_photo_url}
                      alt="License plate"
                      className="w-full h-20 object-cover rounded border border-white/10 hover:border-gold/40 transition-colors"
                    />
                    <p className="text-[10px] text-white/45 mt-0.5 text-center">Plate · safe to screenshot</p>
                  </a>
                )}
              </div>
            )}
            {ride.driver_phone && (
              <p className="text-[11px] text-white/45">Phone = call · Text = SMS your driver — both use your native apps.</p>
            )}
          </div>
        )}
        {/* Delivery progress timeline — visible only on food/delivery rides */}
        {ride.type === "food" && (
          <DeliveryProgressTimeline ride={ride} driverKm={driverKm} />
        )}
        {/* Add-a-stop / waypoints */}
        <WaypointPanel ride={ride} onUpdated={onRideUpdate} />
        {/* Pickup PIN — passenger reads aloud to driver */}
        <PickupPinReveal ride={ride} />
        {/* In-app chat trigger + slide-up panel */}
        <button data-testid="open-chat-btn" onClick={() => setChatOpen?.(true)}
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-xs font-body uppercase tracking-wider rounded border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 transition-colors">
          <MessageSquare size={14} /> Chat with driver {unreadChat > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-sky-500 text-luxury-black text-[10px]">{unreadChat}</span>}
        </button>
        {chatOpen && (
          <RideChat ride={ride} myEmail={myEmail} onClose={() => setChatOpen?.(false)} />
        )}
        {/* Hazard report — passenger drops a community pin where they are */}
        <div className="p-3 border border-amber-500/20 bg-amber-500/5 rounded-md flex items-center justify-between gap-3">
          <div className="text-[11px] text-white/60">
            <p className="text-amber-300 uppercase tracking-wider text-[10px]">Community alerts</p>
            See something on the road? Drop a quick pin.
          </div>
          <HazardReportButton
            lat={(driverPin?.lat) ?? (ride.pickup?.lat) ?? 0}
            lng={(driverPin?.lng) ?? (ride.pickup?.lng) ?? 0}
            compact
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="sos-btn" onClick={onSos}
            className="flex items-center justify-center gap-2 bg-red-500/20 text-red-300 py-3 text-xs font-body uppercase tracking-wider border border-red-500/40 hover:bg-red-500/30 transition-colors">
            <AlertTriangle size={14} /> SOS
          </button>
          <button data-testid="share-trip-btn" onClick={onCopy}
            className="flex items-center justify-center gap-2 bg-white/5 text-white py-3 text-xs font-body uppercase tracking-wider border border-white/15 hover:bg-white/10 transition-colors">
            <Copy size={14} /> Share Trip
          </button>
        </div>
        {/* Cancel ride — only when status allows (i.e. not yet completed) */}
        {ride.status !== "completed" && ride.status !== "cancelled" && (
          <button
            data-testid="cancel-ride-btn"
            onClick={onCancel}
            className="w-full flex items-center justify-center gap-2 bg-rose-500/15 text-rose-300 py-2.5 text-xs font-body uppercase tracking-wider border border-rose-500/30 hover:bg-rose-500/25 transition-colors"
          >
            <X size={14} /> Cancel Ride
          </button>
        )}
      </div>
      <div className="lg:col-span-2">
        <MapView pickup={ride.pickup} dropoff={ride.dropoff} driver={driverPin}
          waypoints={ride.waypoints} height={620}
          recenter={driverPin || ride.pickup} data-testid="active-ride-map">
          <RoutePolyline rideId={ride.id} />
          <HazardLayer center={driverPin || ride.pickup} radiusKm={25} allowConfirm />
        </MapView>
      </div>
    </div>
  );
}
